/**
 * Live-trading one-click placement (live-tennis V1b).
 *
 * An explicitly ARMED interface state in which a ladder/pill price click on a
 * Kalshi market submits a limit order immediately — no ticket, no second
 * click. Design rules, in order of importance:
 *
 *   • Arming is SESSION-ONLY. It always starts disarmed and is never
 *     persisted. Refresh, access loss, identity change, route departure,
 *     board-context change, venue departure, and the toggle disarm it.
 *   • The one-click size is its own explicit setting (persisted), NOT the
 *     ticket's `defaultContracts`: that pref is deliberately blank-fill-only
 *     in the panel, and reusing it here would give the panel's anti-fat-finger
 *     rule a second meaning. Hard-clamped to ONE_CLICK_MAX_CONTRACTS — the
 *     largest explicit per-trader ceiling the relay accepts.
 *   • Submissions are paced by MIN_ORDER_SPACING_MS (the same ≤4/sec spacing
 *     the multi-leg ladder uses on the shared org Kalshi token bucket). A
 *     click landing inside the window is DROPPED and surfaced as "throttled",
 *     never queued — a queue would fire orders after the market moved.
 *   • Every order carries a fresh `lt-` client_order_id so the relay audit
 *     rows (and Kalshi's echo) can be joined back to one-click inputs during
 *     post-session reconciliation.
 *   • postOnly rides the click's ladder semantics: a bid-level click joins
 *     (post_only=true, can't cross), an ask-level click may cross. This store
 *     never overrides what the click site derived.
 *
 * The submit path is accountStore.placeOrder — the same audited relay lane
 * (checkOrderLimit → trades audit row → trackIntent) every manual ticket
 * order takes. There is no second order route.
 */

import { create } from 'zustand';
import {
  hasPendingKalshiPlacementConfirmation,
  useAccountStore,
} from './accountStore';
import { MIN_ORDER_SPACING_MS } from '../views/ValueDashboard/orderLadder';
import type { VenueBook } from '../views/ValueDashboard/types';
import { OrderSubmissionUnknownError } from '../lib/trading/orderSubmissionOutcome';
import {
  buildLt2AuditMeta,
  lt2AuditRequired,
  evaluateIntentGuard,
  type LiveOrderIntent,
} from '../lib/trading/liveIntent';
import { recordLt2Timing, updateLt2Timing } from '../lib/trading/lt2Timing';
import { deriveAttackTarget } from '../lib/trading/attackTarget';
import { getChannelVolume, isChannelSoundEnabled } from '../lib/alerts/alertPreferences';
import { playOrderAckTone, playOrderRejectedTone } from '../lib/audio/tradeAudio';

/** Largest explicit per-trader one-click ceiling supported by the relay. */
export const ONE_CLICK_MAX_CONTRACTS = 10_000;
/** Conservative V1 fallback when no explicit per-trader ceiling is present. */
export const ONE_CLICK_FALLBACK_MAX_CONTRACTS = 500;
export const ONE_CLICK_DEFAULT_CONTRACTS = 100;

const SIZE_STORAGE_KEY = 'galactus:oneClickSizeContracts';

export interface OneClickAck {
  /** Monotonic per-session sequence so the UI can key transitions. */
  seq: number;
  /** 'unverified' (LT2-009): the venue accepted a resting order but it was
   *  no longer visible in the open-orders view 5s later — reconcile via
   *  receipts rather than assuming it is live OR dead. */
  status: 'pending' | 'accepted' | 'rejected' | 'throttled' | 'unverified';
  /** e.g. "BUY 100 NO @ 24¢ SVITOLINA" — built by the click site. */
  label: string;
  message?: string;
  atMs: number;
}

/** The most recent venue-accepted one-click placement — the reconciliation
 *  anchor for the 'unverified' check and any receipt deep-link. */
export interface OneClickLastAccepted {
  clientOrderId: string;
  venueOrderId: string | null;
  ticker: string;
  side: 'yes' | 'no';
  priceCents: number;
  atMs: number;
}

export interface OneClickSubmitInput {
  ticker: string;
  side: 'yes' | 'no';
  priceCents: number;
  postOnly: boolean;
  /** Short human label for the ack line (team/player + side). */
  label: string;
}

interface OneClickStore {
  armed: boolean;
  sizeContracts: number;
  lastAck: OneClickAck | null;
  lastAccepted: OneClickLastAccepted | null;
  arm: () => void;
  disarm: () => void;
  setSizeContracts: (n: number) => void;
  /** Fire a one-click order. No-op (with a truthful ack) when disarmed,
   *  throttled, or the input fails the local guards.
   *
   *  LEGACY V1 shape with no remaining production callsite: every armed
   *  mouse click routes through the intercept adapters into `submitIntent`,
   *  which is where the acknowledgement/timing parity contract lives. Kept
   *  for its pinned tests and as the documented V1 input; new callers use
   *  `submitIntent`. */
  submit: (input: OneClickSubmitInput) => Promise<void>;
  /** D-020 path: fire a normalized LT2 intent through the SUBMIT-TIME guard
   *  (identity, availability, price, maker-role revalidation against the
   *  current book) before the audited placement. Every refusal surfaces a
   *  truthful ack — never a silent drop while armed. */
  submitIntent: (
    intent: LiveOrderIntent,
    book: VenueBook | null | undefined,
  ) => Promise<void>;
  /** Truthful local refusal on the ack line for input paths that fail before
   *  an intent can even be built (hotkey with no displayed price / target).
   *  Never a silent no-op while armed. */
  rejectLocal: (label: string, message: string) => void;
  /** V3 Spacebar: post a non-order lifecycle ack (Pull All pending/summary)
   *  onto the same strip. Pending/accepted statuses only — order intents
   *  keep their own paths. */
  noteAck: (status: 'pending' | 'accepted' | 'rejected', label: string, message?: string) => void;
  /** Live Trading V3 W/E: fire a bounded ATTACK intent — the same guarded,
   *  audited lane as submitIntent, with the attack size, the `__attack` gate
   *  marker, and postOnly false (intentional taker within the bound). Refuses
   *  truthfully when attack is not armed. */
  submitAttackIntent: (
    intent: LiveOrderIntent,
    book: VenueBook | null | undefined,
  ) => Promise<void>;
}

function clampSize(n: number): number {
  if (!Number.isFinite(n)) return ONE_CLICK_DEFAULT_CONTRACTS;
  return Math.max(1, Math.min(ONE_CLICK_MAX_CONTRACTS, Math.round(n)));
}

function loadSize(): number {
  try {
    const raw = localStorage.getItem(SIZE_STORAGE_KEY);
    const n = raw == null ? NaN : parseInt(raw, 10);
    return Number.isFinite(n) ? clampSize(n) : ONE_CLICK_DEFAULT_CONTRACTS;
  } catch {
    return ONE_CLICK_DEFAULT_CONTRACTS;
  }
}

let _ackSeq = 0;
let _lastSubmitAtMs = 0;

/** Registered by attackStore at ITS module init — a lazy accessor instead of
 *  an import, because attackStore already imports this store (for the arm
 *  subordination) and a top-level cycle left this module's exports
 *  uninitialized in attackStore's body. Null = attack machinery not loaded,
 *  which fails closed below exactly like a disarmed attack. */
let _attackState:
  | (() => { attackArmed: boolean; attackSizeContracts: number })
  | null = null;
/**
 * The LOCAL acknowledgement sound — rings the moment a valid intent passes
 * local preflight, for mouse and hotkey alike, which is the parity contract:
 * the first audible feedback must not wait for the relay round trip. It is
 * its OWN channel ('orderAck', a neutral tick) precisely because it fires
 * before the venue answers: the venue-accepted chirp ('orderPlaced') keeps
 * its registry meaning and still rings from placeOrder on success, and the
 * rejection descent stays the failure sound. Three states, three sounds,
 * each independently toggleable.
 */
function playLocalAckTone(): void {
  if (isChannelSoundEnabled('orderAck')) playOrderAckTone(getChannelVolume('orderAck'));
}

/** Audible local refusal — an armed trader's refused keypress must not be
 *  silent-to-the-ear when a click would have rung (perceived input lag).
 *  Shares the 'orderRejected' channel, whose registry description covers
 *  local guard refusals and venue rejections alike. */
function playLocalRefusalTone(): void {
  if (isChannelSoundEnabled('orderRejected')) {
    playOrderRejectedTone(getChannelVolume('orderRejected'));
  }
}

export function _registerAttackStateAccessor(
  fn: () => { attackArmed: boolean; attackSizeContracts: number },
): void {
  _attackState = fn;
}

/** How long after venue acceptance a claimed-resting order may stay invisible
 *  in the open-orders view before the ack flips to 'unverified' (LT2-009). */
export const ONE_CLICK_VERIFY_AFTER_MS = 5_000;

export const useOneClickStore = create<OneClickStore>((set, get) => ({
  armed: false,
  sizeContracts: loadSize(),
  lastAck: null,
  lastAccepted: null,

  arm: () => set({ armed: true, lastAck: null }),
  disarm: () => set({ armed: false, lastAck: null }),

  rejectLocal: (label: string, message: string) => {
    playLocalRefusalTone();
    set({
      lastAck: { seq: ++_ackSeq, status: 'rejected', label, message, atMs: Date.now() },
    });
  },

  noteAck: (status: 'pending' | 'accepted' | 'rejected', label: string, message?: string) => {
    if (status === 'rejected') playLocalRefusalTone();
    set({
      lastAck: { seq: ++_ackSeq, status, label, message, atMs: Date.now() },
    });
  },

  setSizeContracts: (n: number) => {
    const clamped = clampSize(n);
    set({ sizeContracts: clamped });
    try {
      localStorage.setItem(SIZE_STORAGE_KEY, String(clamped));
    } catch {
      /* private mode — session-only size is fine */
    }
  },

  submit: async (input: OneClickSubmitInput) => {
    const { armed, sizeContracts } = get();
    if (!armed) return; // click sites gate on armed; belt-and-suspenders

    const ack = (partial: Omit<OneClickAck, 'seq' | 'atMs'>) =>
      set({ lastAck: { ...partial, seq: ++_ackSeq, atMs: Date.now() } });

    if (!Number.isInteger(input.priceCents) || input.priceCents < 1 || input.priceCents > 99) {
      ack({ status: 'rejected', label: input.label, message: 'Price out of range' });
      return;
    }

    // Pacing — drop, don't queue. The window is measured from the last
    // ACCEPTED submission attempt (not from throttled drops), so a rapid
    // double-click drops exactly the second click.
    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      ack({ status: 'throttled', label: input.label, message: 'Click ignored — pacing (≤4/sec)' });
      return;
    }
    _lastSubmitAtMs = now;

    const count = clampSize(sizeContracts);
    ack({ status: 'pending', label: input.label });
    playLocalAckTone();
    try {
      await useAccountStore.getState().placeOrder({
        ticker: input.ticker,
        side: input.side,
        action: 'buy',
        type: 'limit',
        count,
        price: input.priceCents,
        postOnly: input.postOnly,
        clientOrderId: `lt-${crypto.randomUUID()}`,
      });
      ack({ status: 'accepted', label: input.label });
    } catch (err) {
      ack({
        status: 'rejected',
        label: input.label,
        message: err instanceof Error && err.message ? err.message : 'Order failed',
      });
    }
  },

  submitIntent: async (intent: LiveOrderIntent, book: VenueBook | null | undefined) => {
    const { armed, sizeContracts } = get();
    if (!armed) return; // click sites gate on armed; belt-and-suspenders

    const ack = (partial: Omit<OneClickAck, 'seq' | 'atMs'>): number => {
      const seq = ++_ackSeq;
      set({ lastAck: { ...partial, seq, atMs: Date.now() } });
      return seq;
    };

    // SUBMIT-TIME guard — the render-time pill disable is presentation, this
    // is the enforcement. Refusals are truthful acks, never silent drops.
    const guardMs = Date.now();
    const guard = evaluateIntentGuard(intent, book);
    if (!guard.ok) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: intent.label, message: guard.message });
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'guard-refused',
        inputAtMs: intent.inputAtMs ?? null,
        guardMs,
        ackMs: Date.now(),
        requestMs: null,
        responseMs: null,
        finalMs: Date.now(),
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }

    // Pacing — drop, don't queue (same window as `submit`).
    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      ack({ status: 'throttled', label: intent.label, message: 'Click ignored — pacing (≤4/sec)' });
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'throttled',
        inputAtMs: intent.inputAtMs ?? null,
        guardMs,
        ackMs: Date.now(),
        requestMs: null,
        responseMs: null,
        finalMs: Date.now(),
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }
    _lastSubmitAtMs = now;

    const count = clampSize(sizeContracts);
    // Coid minted BEFORE the call so the audit stamp and the reconciliation
    // anchor share it even if the response is lost mid-flight.
    const clientOrderId = `lt-${crypto.randomUUID()}`;
    ack({ status: 'pending', label: intent.label });
    // LOCAL acknowledgement — before the await, so the sound is the keypress's
    // echo, not the venue's (parity contract; venue truth follows separately).
    playLocalAckTone();
    const ackMs = Date.now();
    const requestMs = Date.now();
    // The timing entry is created NOW, at the local pending acknowledgement,
    // so its render stamp measures the first frame after THIS ack — the
    // input→first-render number the parity review needs — not the first
    // frame after the venue answered. Outcome and response are patched onto
    // this same entry when the relay replies.
    const timingId = recordLt2Timing({
      inputMethod: intent.inputMethod,
      outcome: 'pending',
      inputAtMs: intent.inputAtMs ?? null,
      guardMs,
      ackMs,
      requestMs,
      responseMs: null,
    });
    try {
      const placed = await useAccountStore.getState().placeOrder({
        ticker: intent.ticker,
        side: intent.side,
        action: 'buy',
        type: 'limit',
        count,
        price: intent.priceCents,
        postOnly: intent.postOnly,
        clientOrderId,
        // V1 moneyline and N-outcome moneyline CLICKS also use submitIntent
        // for its stronger submit-time guard, but must not acquire the
        // separate LT2 capability boundary merely because the implementation
        // is shared. HOTKEY intents are that capability, so they are stamped
        // on every market kind (#647) — see lt2AuditRequired.
        auditMeta: lt2AuditRequired(intent)
          ? buildLt2AuditMeta(intent, guard, book)
          : undefined,
      });
      const responseMs = Date.now();
      const venueOrderId =
        placed != null && typeof placed.orderId === 'string' && placed.orderId !== ''
          ? placed.orderId
          : null;
      // LT2-009 reconciliation below decides the RESTING order's terminal
      // moment; a crossing (taker) or id-less acceptance is terminal at the
      // response itself.
      const expectRest = intent.postOnly || guard.role !== 'taker';
      const verifies = venueOrderId != null && expectRest;
      updateLt2Timing(timingId, {
        outcome: 'accepted',
        responseMs,
        ...(verifies ? {} : { finalMs: responseMs, finalOutcome: 'accepted-terminal' }),
      });
      const acceptedSeq = ack({ status: 'accepted', label: intent.label });
      set({
        lastAccepted: {
          clientOrderId,
          venueOrderId,
          ticker: intent.ticker,
          side: intent.side,
          priceCents: intent.priceCents,
          atMs: Date.now(),
        },
      });
      // LT2-009 reconciliation: a maker order the venue accepted should be
      // visible among open orders (placeOrder inserts confirmed resting
      // orders synchronously and refreshOrders re-reads). If it has vanished
      // shortly after acceptance without a newer ack, say so explicitly
      // instead of letting the trader assume it rests. Crossing intents
      // (role 'taker', non-postOnly) legitimately leave the book — skip.
      if (verifies) {
        setTimeout(() => {
          const st = get();
          if (st.lastAck?.seq !== acceptedSeq) {
            // A newer ack owns the line; this order's visibility question is
            // abandoned, and pretending it settled would be false telemetry.
            updateLt2Timing(timingId, { finalMs: Date.now(), finalOutcome: 'superseded' });
            return;
          }
          const account = useAccountStore.getState();
          // Resting is visible; a fill is also a fully accounted-for exit —
          // only authoritative evidence counts. The placement fence temporarily
          // re-injects an accepted order into openOrders while venue reads lag;
          // that projection must not prove itself visible.
          const venueOrderVisible =
            !hasPendingKalshiPlacementConfirmation(venueOrderId) &&
            (account.openOrders ?? []).some((o) => o.orderId === venueOrderId);
          const visible =
            venueOrderVisible ||
            (account.recentFills ?? []).some((f) => f.orderId === venueOrderId);
          if (!visible) {
            set({
              lastAck: {
                seq: ++_ackSeq,
                status: 'unverified',
                label: intent.label,
                message: 'Accepted but not visible on the book — check receipts',
                atMs: Date.now(),
              },
            });
          }
          updateLt2Timing(timingId, {
            finalMs: Date.now(),
            finalOutcome: visible ? 'resting-verified' : 'unverified',
            ...(visible ? {} : { outcome: 'unverified' as const }),
          });
        }, ONE_CLICK_VERIFY_AFTER_MS);
      }
    } catch (err) {
      const outcome = err instanceof OrderSubmissionUnknownError ? 'unverified' : 'rejected';
      ack({
        status: outcome,
        label: intent.label,
        message: err instanceof Error && err.message ? err.message : 'Order failed',
      });
      const failMs = Date.now();
      updateLt2Timing(timingId, {
        outcome,
        responseMs: failMs,
        finalMs: failMs,
        finalOutcome: outcome,
      });
    }
  },

  submitAttackIntent: async (intent: LiveOrderIntent, book: VenueBook | null | undefined) => {
    const { armed } = get();
    const attackState = _attackState?.() ?? null;
    // The listener gates on both arms; belt-and-suspenders here so no other
    // caller can fire an attack from a half-armed (or not-even-loaded) state.
    if (!armed || attackState == null || !attackState.attackArmed) return;
    const { attackSizeContracts } = attackState;

    const ack = (partial: Omit<OneClickAck, 'seq' | 'atMs'>): number => {
      const seq = ++_ackSeq;
      set({ lastAck: { ...partial, seq, atMs: Date.now() } });
      return seq;
    };

    // An attack intent must carry its own provenance — a missing payload is a
    // caller bug, refused rather than silently downgraded to a passive order.
    if (intent.attack == null || intent.postOnly) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: intent.label, message: 'Malformed attack intent' });
      return;
    }
    const guardMs = Date.now();
    // ACTION-TIME PROOF: re-derive the bounded target from the book handed in
    // at THIS boundary, and refuse if it no longer matches what the keypress
    // computed. The listener's derivation is at most one render frame older,
    // but the packet's contract is a fresh snapshot at action time — recompute
    // OR block, never a stale bound. Blocking is the safe half: submitting a
    // silently recomputed price the trader never saw acknowledged would trade
    // one staleness for another.
    const freshTarget = deriveAttackTarget(book ?? null, intent.side, false);
    if (
      freshTarget == null ||
      freshTarget.targetPriceCents !== intent.priceCents ||
      freshTarget.referencePriceCents !== intent.attack.referencePriceCents ||
      freshTarget.depth !== intent.attack.depth
    ) {
      playLocalRefusalTone();
      ack({
        status: 'rejected',
        label: intent.label,
        message: 'Book changed — attack not sent; re-press at the new level',
      });
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'guard-refused',
        inputAtMs: intent.inputAtMs ?? null,
        guardMs,
        ackMs: Date.now(),
        requestMs: null,
        responseMs: null,
        finalMs: Date.now(),
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }

    // Same submit-time guard as every other intent: identity, availability,
    // price range, and role. postOnly is false, so an intentional taker role
    // passes — the aggression bound lives in the resolver-computed target
    // (reference + accepted depth) and the relay's attack ceiling.
    const guard = evaluateIntentGuard(intent, book);
    if (!guard.ok) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: intent.label, message: guard.message });
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'guard-refused',
        inputAtMs: intent.inputAtMs ?? null,
        guardMs,
        ackMs: Date.now(),
        requestMs: null,
        responseMs: null,
        finalMs: Date.now(),
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }

    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      ack({ status: 'throttled', label: intent.label, message: 'Keypress ignored — pacing (≤4/sec)' });
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'throttled',
        inputAtMs: intent.inputAtMs ?? null,
        guardMs,
        ackMs: Date.now(),
        requestMs: null,
        responseMs: null,
        finalMs: Date.now(),
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }
    _lastSubmitAtMs = now;

    // The ATTACK size — never the one-click size. Clamped again here so a
    // hand-edited persisted value cannot exceed the client mirror; the relay
    // enforces the real ceiling regardless.
    const count = Math.max(1, Math.round(attackSizeContracts));
    const clientOrderId = `lt-${crypto.randomUUID()}`;
    ack({ status: 'pending', label: intent.label });
    playLocalAckTone();
    const ackMs = Date.now();
    const requestMs = Date.now();
    // Same contract as submitIntent: the entry exists from the local pending
    // acknowledgement onward, so renderMs measures the ack frame, never the
    // response frame.
    const timingId = recordLt2Timing({
      inputMethod: intent.inputMethod,
      outcome: 'pending',
      inputAtMs: intent.inputAtMs ?? null,
      guardMs,
      ackMs,
      requestMs,
      responseMs: null,
    });
    try {
      const placed = await useAccountStore.getState().placeOrder({
        ticker: intent.ticker,
        side: intent.side,
        action: 'buy',
        type: 'limit',
        count,
        price: intent.priceCents,
        postOnly: false,
        clientOrderId,
        // Every hotkey intent is LT2-stamped (lt2AuditRequired); the attack
        // marker is ADDITIONAL — the relay's gate input and the durable
        // record of reference/depth this bounded action was computed from.
        auditMeta: buildLt2AuditMeta(intent, guard, book),
        // The durable action-time evidence: the marker the relay's arithmetic
        // gate checks, plus the minimal exact book snapshot the target was
        // proven against — both raw sides' top levels, so a reader can
        // reconstruct the chosen side's reference, the target, and the
        // opposing liquidity at it without trusting any derived field.
        attackMeta: {
          depth: intent.attack.depth,
          referencePriceCents: intent.attack.referencePriceCents,
          book: {
            atMs: Date.now(),
            marketTicker: book?.marketTicker ?? null,
            freshness: book?.freshness ?? null,
            lastPriceCents: book?.lastPriceCents ?? null,
            yes: (book?.yes ?? []).slice(0, 3).map((l) => ({ p: l.priceCents, s: l.size })),
            no: (book?.no ?? []).slice(0, 3).map((l) => ({ p: l.priceCents, s: l.size })),
          },
        },
      });
      const responseMs = Date.now();
      const venueOrderId =
        placed != null && typeof placed.orderId === 'string' && placed.orderId !== ''
          ? placed.orderId
          : null;
      // LT2-009 reconciliation applies to the ESTABLISH half only: a taker
      // fill legitimately never rests. guard.role is 'taker' when displayed
      // opposing liquidity crossed the target — skip; maker/unknown means the
      // order should be visible resting at the target.
      const expectRest = guard.role !== 'taker';
      const verifies = venueOrderId != null && expectRest;
      updateLt2Timing(timingId, {
        outcome: 'accepted',
        responseMs,
        ...(verifies ? {} : { finalMs: responseMs, finalOutcome: 'accepted-terminal' }),
      });
      const acceptedSeq = ack({ status: 'accepted', label: intent.label });
      set({
        lastAccepted: {
          clientOrderId,
          venueOrderId,
          ticker: intent.ticker,
          side: intent.side,
          priceCents: intent.priceCents,
          atMs: Date.now(),
        },
      });
      if (verifies) {
        setTimeout(() => {
          const st = get();
          if (st.lastAck?.seq !== acceptedSeq) {
            updateLt2Timing(timingId, { finalMs: Date.now(), finalOutcome: 'superseded' });
            return;
          }
          const account = useAccountStore.getState();
          const venueOrderVisible =
            !hasPendingKalshiPlacementConfirmation(venueOrderId) &&
            (account.openOrders ?? []).some((o) => o.orderId === venueOrderId);
          const visible =
            venueOrderVisible ||
            (account.recentFills ?? []).some((f) => f.orderId === venueOrderId);
          if (!visible) {
            set({
              lastAck: {
                seq: ++_ackSeq,
                status: 'unverified',
                label: intent.label,
                message: 'Accepted but not visible on the book — check receipts',
                atMs: Date.now(),
              },
            });
          }
          updateLt2Timing(timingId, {
            finalMs: Date.now(),
            finalOutcome: visible ? 'resting-verified' : 'unverified',
            ...(visible ? {} : { outcome: 'unverified' as const }),
          });
        }, ONE_CLICK_VERIFY_AFTER_MS);
      }
    } catch (err) {
      const outcome = err instanceof OrderSubmissionUnknownError ? 'unverified' : 'rejected';
      ack({
        status: outcome,
        label: intent.label,
        message: err instanceof Error && err.message ? err.message : 'Order failed',
      });
      const failMs = Date.now();
      updateLt2Timing(timingId, {
        outcome,
        responseMs: failMs,
        finalMs: failMs,
        finalOutcome: outcome,
      });
    }
  },
}));
